import React from 'react';
import { Box, Modal, IconButton, Typography, Button, Slider, Stack } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { isMobile } from 'react-device-detect';
import { IUserState } from '../../Core/State/IUserState';
import { IPokerUserSettings } from '../../Core/State/IPokerUserSettings';
import { UserSettingsManager } from '../../Utils/UserSettingsManager';
import { t } from '../../i18n';

interface SettingsModalProps {
    open: boolean;
    handleClose: () => void;
    userState: IUserState;
    updateUserSettings: (settings: Partial<IPokerUserSettings>) => void;
    userSettingsManager: UserSettingsManager;
}

interface SettingsModalState {
    notificationFrequency: number;
    hasChanges: boolean;
}

class SettingsModal extends React.Component<SettingsModalProps, SettingsModalState> {
    constructor(props: SettingsModalProps) {
        super(props);
        this.state = {
            notificationFrequency: props.userSettingsManager.getNotificationFrequencyUIValue(),
            hasChanges: false
        };
    }

    componentDidUpdate(prevProps: SettingsModalProps) {
        if (prevProps.userState.pokerUserSettings !== this.props.userState.pokerUserSettings) {
            this.setState({
                notificationFrequency: this.props.userSettingsManager.getNotificationFrequencyUIValue(),
                hasChanges: false
            });
        }
    }

    private handleNotificationFrequencyChange = (_: Event, value: number | number[]) => {
        const newFrequency = Array.isArray(value) ? value[0] : value;
        this.setState({
            notificationFrequency: newFrequency,
            hasChanges: true
        });
    };

    private handleSave = () => {
        const { notificationFrequency } = this.state;
        this.props.updateUserSettings({
            notificationFrequency: notificationFrequency / 100
        });
        this.setState({ hasChanges: false });
    };

    private handleClose = () => {
        this.setState({
            notificationFrequency: this.props.userSettingsManager.getNotificationFrequencyUIValue(),
            hasChanges: false
        });
        this.props.handleClose();
    };

    render() {
        const { open, userSettingsManager } = this.props;
        const { notificationFrequency, hasChanges } = this.state;
        const sliderConfig = userSettingsManager.getNotificationFrequencySliderConfig();

        return (
            <Modal
                open={open}
                onClose={this.handleClose}
                aria-labelledby="settings-modal-title"
                aria-describedby="settings-modal-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: isMobile ? '90%' : 450,
                    maxWidth: isMobile ? '90%' : 450,
                    bgcolor: '#1E1F25',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    borderRadius: '8px',
                    boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.5)',
                    p: isMobile ? 2 : 4,
                    color: 'white',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    boxSizing: 'border-box',
                }}>
                    <IconButton
                        aria-label="close"
                        onClick={this.handleClose}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: 'rgba(255, 255, 255, 0.7)',
                            '&:hover': {
                                color: 'white',
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>

                    <Typography
                        id="settings-modal-title"
                        variant="h5"
                        component="h2"
                        sx={{
                            fontWeight: 700,
                            fontFamily: 'Inter, serif',
                            mb: 3
                        }}
                    >
                        {t('SettingsModal:title')}
                    </Typography>

                    <Stack spacing={4} sx={{ width: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
                        <Box sx={{ width: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} sx={{ width: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontWeight: 600,
                                        fontFamily: 'Inter, serif',
                                        fontSize: isMobile ? '13px' : '14px',
                                        flex: 1,
                                        minWidth: 0,
                                        pr: 1
                                    }}
                                >
                                    {t('SettingsModal:notificationFrequency')}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'rgba(255, 255, 255, 0.7)',
                                        fontFamily: 'Inter, serif',
                                        fontSize: isMobile ? '13px' : '14px',
                                        minWidth: '40px',
                                        textAlign: 'right',
                                        flexShrink: 0
                                    }}
                                >
                                    {Math.round(notificationFrequency)}%
                                </Typography>
                            </Stack>
                            <Box sx={{ width: '100%', boxSizing: 'border-box', px: isMobile ? 2 : 1.5 }}>
                                <Slider
                                    value={notificationFrequency}
                                    onChange={this.handleNotificationFrequencyChange}
                                    min={sliderConfig.min}
                                    max={sliderConfig.max}
                                    step={sliderConfig.step}
                                    marks={sliderConfig.marks}
                                    sx={{
                                        color: '#F93751',
                                        width: '100%',
                                        boxSizing: 'border-box',
                                        '& .MuiSlider-markLabel': {
                                            fontFamily: 'Inter, serif',
                                            fontSize: isMobile ? '10px' : '12px',
                                            color: 'rgba(255, 255, 255, 0.5)'
                                        }
                                    }}
                                />
                            </Box>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: 'rgba(255, 255, 255, 0.5)',
                                    fontFamily: 'Inter, serif',
                                    fontSize: '12px',
                                    display: 'block',
                                    mt: 1
                                }}
                            >
                                {userSettingsManager.getNotificationFrequencyDescription(notificationFrequency)}
                            </Typography>
                        </Box>
                    </Stack>

                    {/* Action Buttons */}
                    <Stack 
                        direction={isMobile ? 'column' : 'row'} 
                        spacing={2} 
                        mt={4} 
                        justifyContent="flex-end"
                        sx={{ width: '100%', boxSizing: 'border-box', overflow: 'hidden' }}
                    >
                        <Button
                            variant="contained"
                            onClick={this.handleSave}
                            disabled={!hasChanges}
                            sx={{
                                backgroundColor: '#F93751',
                                color: 'white',
                                fontWeight: 700,
                                fontSize: isMobile ? '13px' : '14px',
                                textTransform: 'none',
                                fontFamily: 'Inter, serif',
                                width: isMobile ? '100%' : 'auto',
                                '&:hover': {
                                    backgroundColor: '#d01347'
                                },
                                '&:disabled': {
                                    backgroundColor: 'rgba(249, 55, 81, 0.3)',
                                    color: 'rgba(255, 255, 255, 0.3)'
                                }
                            }}
                        >
                            {t('actions.saveChanges')}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={this.handleClose}
                            sx={{
                                color: 'white',
                                borderColor: 'rgba(255, 255, 255, 0.3)',
                                fontWeight: 600,
                                fontSize: isMobile ? '13px' : '14px',
                                textTransform: 'none',
                                fontFamily: 'Inter, serif',
                                width: isMobile ? '100%' : 'auto',
                                '&:hover': {
                                    borderColor: 'rgba(255, 255, 255, 0.5)',
                                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                                }
                            }}
                        >
                            {t('actions.close')}
                        </Button>
                    </Stack>
                </Box>
            </Modal>
        );
    }
}

export default SettingsModal;

